import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, InputNumber, Message, Panel, SelectPicker, Tooltip, Whisper } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCloseDates, useCreateTimeOffRequest, useEmployees, useResubmitTimeOffRequest, useTimeOffRequests, useUpdateTimeOffRequest } from '../hooks/useQueries';
import { showToast } from '../utils/toast';
import PageHeader from '../components/shared/PageHeader';
import TimeOffCreateHelpModal from '../components/timeoff/TimeOffCreateHelpModal';
import TimeOffRangeCalendar from '../components/timeoff/TimeOffRangeCalendar';
import {
  closeDateKeySet,
  countDaysInclusive,
  countWeekdaysInclusive,
  formatDateOnly,
  formatLongDate,
  isSameDay,
  keyForDate,
  parseLocalDateOnly,
} from '../utils/timeOffDates';
import './TimeOffPage.css';

const WORKDAY_HOURS = 8;
const DEFAULT_REQUEST_TYPE = 'time off';

export default function TimeOffCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const createRequest = useCreateTimeOffRequest();
  const resubmitRequest = useResubmitTimeOffRequest();
  const updateRequest = useUpdateTimeOffRequest();
  const requestsQuery = useTimeOffRequests({ enabled: true });
  const closeDatesQuery = useCloseDates({ enabled: true });
  const editRequestId = String(searchParams.get('editRequestId') || '').trim();
  const isEditMode = Boolean(editRequestId);
  const canScheduleForOthers = !isEditMode && (auth.hasPermission('manage_all_employees') || auth.hasPermission('manage_coached_employees'));
  const employeesQuery = useEmployees({ enabled: canScheduleForOthers });

  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [totalHoursInput, setTotalHoursInput] = useState(null);
  const [notes, setNotes] = useState('');
  const [needsApproval, setNeedsApproval] = useState(true);
  const [addToCompanyCalendar, setAddToCompanyCalendar] = useState(true);
  const [wizardStep, setWizardStep] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [closeDateCoverageModalOpen, setCloseDateCoverageModalOpen] = useState(false);
  const [prefilledEditRequestId, setPrefilledEditRequestId] = useState(null);
  const [targetEmployeeId, setTargetEmployeeId] = useState(auth.employee?.id || null);
  const [editNote, setEditNote] = useState('');

  const actorEmployeeId = String(auth.employee?.id || '').trim();

  useEffect(() => {
    // In edit mode the target is whoever owns the request; set once it loads.
    if (isEditMode) return;

    if (!targetEmployeeId && auth.employee?.id) {
      setTargetEmployeeId(auth.employee.id);
    }
  }, [auth.employee?.id, isEditMode, targetEmployeeId]);

  const scheduleTargetOptions = useMemo(() => {
    if (!canScheduleForOthers) return [];

    const rows = Array.isArray(employeesQuery.data)
      ? employeesQuery.data.filter((row) => row?.is_active !== false)
      : [];
    const byId = new Map();

    for (const row of rows) {
      const value = String(row?.id || '').trim();
      if (!value) continue;
      const label = `${String(row?.first_name || '').trim()} ${String(row?.last_name || '').trim()}`.trim() || String(row?.email || '').trim() || 'Unknown User';
      if (!byId.has(value)) {
        byId.set(value, { value, label });
      }
    }

    if (actorEmployeeId && auth.employee?.is_active !== false && !byId.has(actorEmployeeId)) {
      const actorLabel = `${String(auth.employee?.first_name || '').trim()} ${String(auth.employee?.last_name || '').trim()}`.trim()
        || String(auth.employee?.email || '').trim()
        || 'Myself';
      byId.set(actorEmployeeId, { value: actorEmployeeId, label: `${actorLabel} (Me)` });
    }

    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [actorEmployeeId, auth.employee?.email, auth.employee?.first_name, auth.employee?.is_active, auth.employee?.last_name, canScheduleForOthers, employeesQuery.data]);

  const selectedEmployeeId = String(targetEmployeeId || actorEmployeeId || '').trim();

  useEffect(() => {
    if (!canScheduleForOthers || !scheduleTargetOptions.length) return;
    const hasSelection = scheduleTargetOptions.some((option) => option.value === selectedEmployeeId);
    if (!hasSelection) {
      setTargetEmployeeId(actorEmployeeId || scheduleTargetOptions[0].value);
    }
  }, [actorEmployeeId, canScheduleForOthers, scheduleTargetOptions, selectedEmployeeId]);

  const selectedEmployee = useMemo(() => {
    if (!selectedEmployeeId) return null;
    if (selectedEmployeeId === actorEmployeeId) return auth.employee || null;

    const rows = Array.isArray(employeesQuery.data) ? employeesQuery.data : [];
    return rows.find((row) => String(row?.id || '').trim() === selectedEmployeeId) || null;
  }, [actorEmployeeId, auth.employee, employeesQuery.data, selectedEmployeeId]);

  const selectedEmployeeLabel = useMemo(() => {
    const firstName = String(selectedEmployee?.first_name || '').trim();
    const lastName = String(selectedEmployee?.last_name || '').trim();
    return `${firstName} ${lastName}`.trim() || String(selectedEmployee?.email || '').trim() || 'Selected employee';
  }, [selectedEmployee]);

  const actingOnBehalf = Boolean(
    !isEditMode
      && actorEmployeeId
      && selectedEmployeeId
      && selectedEmployeeId !== actorEmployeeId
  );

  const editableRequest = useMemo(() => {
    if (!isEditMode) return null;
    const employeeId = auth.employee?.id;
    if (!employeeId) return null;

    const request = (requestsQuery.data || []).find((row) => String(row.id) === String(editRequestId));
    if (!request) return null;

    const isOwner = String(request.employee_id) === String(employeeId);
    const status = String(request.status || '').toLowerCase();
    const isPending = status === 'pending';
    // Anyone may modify their own pending request; an admin may also edit
    // anybody's request, approved ones included.
    const adminCanEdit = auth.isAdmin() && (isPending || status === 'approved');
    if (!(isOwner && isPending) && !adminCanEdit) return null;

    return request;
  }, [auth, editRequestId, isEditMode, requestsQuery.data]);

  // Editing in place rather than by resubmitting: an admin changing somebody
  // else's request, or any approved one. The status stays as it is, and when it
  // is somebody else's they are emailed what changed.
  const editingOther = Boolean(editableRequest) && String(editableRequest.employee_id) !== actorEmployeeId;
  const isAdminEdit = Boolean(editableRequest)
    && (editingOther || String(editableRequest.status || '').toLowerCase() !== 'pending');
  const editOwnerName = editableRequest
    ? `${String(editableRequest.first_name || '').trim()} ${String(editableRequest.last_name || '').trim()}`.trim()
      || String(editableRequest.email || '').trim() || 'this employee'
    : null;
  const editStatusLabel = editableRequest && String(editableRequest.status || '').toLowerCase() === 'approved'
    ? (String(editableRequest.approval_reason || '').trim().toLowerCase() === 'no approval required' ? 'entered' : 'approved')
    : 'pending';

  useEffect(() => {
    if (isEditMode && editableRequest?.employee_id) setTargetEmployeeId(String(editableRequest.employee_id));
  }, [isEditMode, editableRequest?.employee_id]);

  useEffect(() => {
    if (!isEditMode) {
      setPrefilledEditRequestId(null);
      return;
    }

    if (!editableRequest) return;

    const currentEditableRequestId = String(editableRequest.id || '');
    if (!currentEditableRequestId || prefilledEditRequestId === currentEditableRequestId) return;

    const start = parseLocalDateOnly(editableRequest.start_date);
    const end = parseLocalDateOnly(editableRequest.end_date);
    const storedHours = Number(editableRequest.total_hours);

    setRangeStart(start);
    setRangeEnd(end);
    setTotalHoursInput(
      Number.isFinite(storedHours)
        ? storedHours
        : (start && end ? countWeekdaysInclusive(start, end) * WORKDAY_HOURS : null)
    );
    setNotes(String(editableRequest.notes || ''));
    setNeedsApproval(true);
    setAddToCompanyCalendar(Boolean(editableRequest.add_to_company_calendar));
    setWizardStep(0);
    setPrefilledEditRequestId(currentEditableRequestId);
  }, [editableRequest, isEditMode, prefilledEditRequestId]);

  const editRequestNotAvailable = isEditMode && !requestsQuery.isLoading && !editableRequest;

  // Lifted out of totalHours below so the review screen can show where the
  // calculated figure came from rather than just asserting it.
  const workingDayCount = useMemo(
    () => countWeekdaysInclusive(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  const selectedDayCount = useMemo(
    () => countDaysInclusive(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  const totalHours = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    return workingDayCount * WORKDAY_HOURS;
  }, [rangeStart, rangeEnd, workingDayCount]);

  const canGoNext = Boolean(rangeStart && rangeEnd);
  const trimmedNotes = notes.trim();
  const isReadyToSubmit = Boolean(
    rangeStart
      && rangeEnd
      && Number.isFinite(Number(totalHoursInput))
      && Number(totalHoursInput) > 0
      && trimmedNotes.length > 0
      && (!isEditMode || Boolean(editableRequest))
  );

  const overlapSummary = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [];

    const requests = requestsQuery.data || [];
    const currentEmployeeId = selectedEmployeeId || auth.employee?.id;
    const users = new Map();

    const getOverlapRange = (aStart, aEnd, bStart, bEnd) => {
      const start = aStart > bStart ? aStart : bStart;
      const end = aEnd < bEnd ? aEnd : bEnd;
      return end < start ? null : { start, end };
    };

    for (const request of requests) {
      if (isEditMode && editableRequest?.id && String(request.id) === String(editableRequest.id)) {
        continue;
      }

      const requestStart = parseLocalDateOnly(request.start_date);
      const requestEnd = parseLocalDateOnly(request.end_date);
      if (!requestStart || !requestEnd || requestEnd < requestStart) continue;

      const isMine = currentEmployeeId && String(request.employee_id) === String(currentEmployeeId);

      const status = String(request.status || '').toLowerCase();
      const isApproved = status === 'approved';
      const isPending = status === 'pending';
      if (status === 'cancelled' || status === 'rejected') continue;

      // Only warn about other employees when their request is already approved.
      // Keep self-overlap protection for both pending and approved requests.
      if (!isMine && !isApproved) continue;
      if (isMine && !isApproved && !isPending) continue;

      const overlap = getOverlapRange(rangeStart, rangeEnd, requestStart, requestEnd);
      if (!overlap) continue;

      const firstName = request.first_name || request.employee_first_name || '';
      const lastName = request.last_name || request.employee_last_name || '';
      const fullName = `${String(firstName).trim()} ${String(lastName).trim()}`.trim();
      const userLabel = fullName || request.email || request.employee_email || 'Unknown User';
      const userKey = String(request.employee_id || userLabel);

      if (!users.has(userKey)) {
        users.set(userKey, {
          userKey,
          userLabel,
          isSelf: Boolean(isMine),
          overlapRanges: [],
          overlapDays: 0,
        });
      }

      const row = users.get(userKey);
      if (isMine) row.isSelf = true;
      row.overlapRanges.push(overlap);
      row.overlapDays += countDaysInclusive(overlap.start, overlap.end);
    }

    return Array.from(users.values()).sort((a, b) => a.userLabel.localeCompare(b.userLabel));
  }, [auth.employee?.id, editableRequest?.id, isEditMode, rangeEnd, rangeStart, requestsQuery.data, selectedEmployeeId]);

  const hasTargetOverlap = useMemo(
    () => overlapSummary.some((item) => item.isSelf),
    [overlapSummary]
  );

  const isVacationFullyCoveredByCloseDates = useMemo(() => {
    if (!rangeStart || !rangeEnd) return false;

    const selectedDayKeys = new Set();
    for (let cursor = new Date(rangeStart); cursor <= rangeEnd; cursor.setDate(cursor.getDate() + 1)) {
      const key = keyForDate(cursor);
      if (key) selectedDayKeys.add(key);
    }

    if (selectedDayKeys.size === 0) return false;

    const coveredDayKeys = closeDateKeySet(closeDatesQuery.data);

    for (const selectedDayKey of selectedDayKeys) {
      if (!coveredDayKeys.has(selectedDayKey)) return false;
    }

    return true;
  }, [closeDatesQuery.data, rangeEnd, rangeStart]);

  const continueToDetails = () => {
    if (!canGoNext) return;

    if (isVacationFullyCoveredByCloseDates) {
      setCloseDateCoverageModalOpen(true);
      return;
    }

    if (overlapSummary.length > 0) {
      setOverlapModalOpen(true);
      return;
    }

    setWizardStep(1);
  };

  const confirmOverlapAndContinue = () => {
    if (hasTargetOverlap) {
      setOverlapModalOpen(false);
      return;
    }
    setOverlapModalOpen(false);
    setWizardStep(1);
  };

  const submitRequest = async () => {
    const payload = {
      employee_id: selectedEmployeeId,
      request_type: DEFAULT_REQUEST_TYPE,
      start_date: formatDateOnly(rangeStart),
      end_date: formatDateOnly(rangeEnd),
      notes: trimmedNotes,
      total_hours: Number(totalHoursInput),
      start_minutes: null,
      end_minutes: null,
      needs_approval: needsApproval,
      add_to_company_calendar: addToCompanyCalendar,
    };

    if (isAdminEdit) {
      const saved = await updateRequest.mutateAsync({
        id: editableRequest.id,
        start_date: payload.start_date,
        end_date: payload.end_date,
        notes: payload.notes,
        total_hours: payload.total_hours,
        start_minutes: null,
        end_minutes: null,
        edit_note: editingOther ? editNote.trim() || undefined : undefined,
      });
      showToast('success', editingOther ? `Request updated. ${editOwnerName} has been emailed about the change.` : 'Request updated.');
      if (saved?.calendar_warning) showToast('warning', saved.calendar_warning, { duration: 9000 });
      navigate(editingOther ? `/time-off/requests?employee=${encodeURIComponent(String(editableRequest.employee_id))}` : '/time-off/requests');
      return;
    }

    if (isEditMode) {
      if (!editableRequest?.id) return;
      await resubmitRequest.mutateAsync({ id: editableRequest.id, ...payload });
    } else {
      await createRequest.mutateAsync(payload);
    }

    navigate('/time-off/requests');
  };

  const submit = async () => {
    if (!isReadyToSubmit) return;

    await submitRequest();
  };

  const submitError = (isAdminEdit ? updateRequest.error : isEditMode ? resubmitRequest.error : createRequest.error) || null;
  const submitLoading = isAdminEdit ? updateRequest.isPending : isEditMode ? resubmitRequest.isPending : createRequest.isPending;
  const cancelModify = () => navigate(editingOther
    ? `/time-off/requests?employee=${encodeURIComponent(String(editableRequest.employee_id))}`
    : '/time-off/requests');

  return (
    <div className={`bdc-page ${isEditMode ? 'timeoff-create-page--editing' : ''}`}>
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Time Off', to: '/time-off' },
          { label: isEditMode ? 'Modify Request' : 'New Request' },
        ]}
        icon={isEditMode ? 'bi-pencil-square' : 'bi-calendar-plus'}
        title={isAdminEdit ? 'Edit Time Off Request' : isEditMode ? 'Modify Time Off Request' : 'Create Time Off Request'}
        note={editingOther ? `for ${editOwnerName}` : null}
        actions={(
          <>
            {canScheduleForOthers ? (
              <div className="timeoff-create-target">
                <div className="text-muted mb-1">Requesting For</div>
                <SelectPicker
                  data={scheduleTargetOptions}
                  value={selectedEmployeeId || null}
                  onChange={(value) => setTargetEmployeeId(value || actorEmployeeId || null)}
                  searchable
                  cleanable={false}
                  disabled={employeesQuery.isLoading}
                  style={{ width: '100%' }}
                  placeholder="Select employee"
                />
              </div>
            ) : null}

            <button type="button" className="bdc-btn" onClick={() => setHelpOpen(true)}>
              <i className="bi bi-question-circle" aria-hidden="true" />
              Help
            </button>
          </>
        )}
      />

      <TimeOffCreateHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        canScheduleForOthers={canScheduleForOthers}
        isEditMode={isEditMode}
      />

      {submitError ? (
        <Message type="error" header="Error" showIcon>
          {String(submitError?.message || submitError)}
        </Message>
      ) : null}

      {canScheduleForOthers && employeesQuery.error ? (
        <Message type="error" showIcon>
          {String(employeesQuery.error?.message || employeesQuery.error || 'Unable to load employees you can manage.')}
        </Message>
      ) : null}

      {editRequestNotAvailable ? (
        <Message type="error" showIcon>
          The request you are trying to edit is no longer available, or you can't edit it.
        </Message>
      ) : null}

      {wizardStep === 0 ? (
        <Panel bordered className="timeoff-create-calendar-panel">
          <TimeOffRangeCalendar
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onRangeChange={(start, end) => {
              setRangeStart(start);
              setRangeEnd(end);
            }}
            requests={requestsQuery.data || []}
            closeDates={closeDatesQuery.data || []}
            employeeId={selectedEmployeeId || auth.employee?.id}
            editingRequest={isEditMode ? editableRequest : null}
          />

          <div className="d-flex justify-content-end mt-3">
            <Button appearance="primary" size="lg" disabled={!canGoNext} onClick={continueToDetails}>
              Next
            </Button>
          </div>
        </Panel>
      ) : null}

      {wizardStep === 1 ? (
        <Panel bordered>
          {/* The dates, first thing.

              This screen is where somebody commits to the request, and until
              now the only thing on it that mentioned the dates was a line of
              small print under the hours box. The calendar is a step behind, so
              checking what was picked meant going back - which loses nothing
              but is exactly the sort of round trip a review screen exists to
              save. The day counts come with it because they are what the
              calculated hours are derived from. */}
          <div className="timeoff-review-dates">
            <i className="bi bi-calendar-range" aria-hidden="true" />
            <div className="timeoff-review-dates__body">
              <div className="timeoff-review-dates__range">
                {rangeStart && rangeEnd
                  ? (isSameDay(rangeStart, rangeEnd)
                    ? formatLongDate(rangeStart)
                    : `${formatLongDate(rangeStart)} to ${formatLongDate(rangeEnd)}`)
                  : '-'}
              </div>
              {rangeStart && rangeEnd ? (
                <div className="timeoff-review-dates__meta">
                  {selectedDayCount} day{selectedDayCount === 1 ? '' : 's'} selected
                  {/* Only worth saying when the two differ. On a Mon-Fri
                      request "5 days, 5 working days" is the same fact twice. */}
                  {workingDayCount !== selectedDayCount
                    ? ` · ${workingDayCount} working day${workingDayCount === 1 ? '' : 's'}`
                    : ''}
                </div>
              ) : null}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="text-muted mb-1">Request Type</div>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={(
                  <Tooltip>
                    For legal reasons, tracked time off is not differentiated by sick or vacation. If you are expecting to be reimbursed for this time, make sure to check Needs Review below.
                  </Tooltip>
                )}
              >
                <div className="rs-input" style={{ paddingTop: 8, paddingBottom: 8, cursor: 'help' }}>
                  Time off
                </div>
              </Whisper>
            </div>

            <div className="col-12 col-md-6">
              <div className="text-muted mb-1">Calculated Working Hours</div>
              <InputNumber
                min={0}
                step={0.25}
                value={totalHoursInput}
                // Held as typed rather than coerced per keystroke: the field is
                // controlled by what goes back into it, and Number("7.") is 7,
                // so converting here deletes the decimal point as it's typed
                // and nothing below a whole number can ever be entered. Every
                // reader of this value already coerces it.
                onChange={(value) => setTotalHoursInput(value === '' || value == null ? null : value)}
                // Tidy up what a half-typed entry left behind ("7." -> 7).
                onBlur={() => setTotalHoursInput((prev) => {
                  if (prev === '' || prev == null) return null;
                  const parsed = Number(prev);
                  return Number.isFinite(parsed) ? parsed : null;
                })}
                placeholder="Enter hours"
                style={{ width: '100%' }}
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Calculated hours for selected dates: {totalHours == null ? '-' : totalHours}
              </div>
            </div>

            {isAdminEdit ? null : (
            <div className="col-12 d-flex align-items-center justify-content-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <Checkbox
                  checked={needsApproval}
                  onChange={(_value, checked) => setNeedsApproval(Boolean(checked))}
                >
                  Needs review?
                </Checkbox>
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={(
                    <Tooltip>
                      Only check this box if this time off request is expecting a reimbursement.
                    </Tooltip>
                  )}
                >
                  <i className="bi bi-info-circle-fill timeoff-help-icon" aria-label="Needs review help" />
                </Whisper>
              </div>

              <Checkbox
                checked={addToCompanyCalendar}
                onChange={(_value, checked) => setAddToCompanyCalendar(Boolean(checked))}
              >
                Add to company calendar?
              </Checkbox>
            </div>
            )}

            <div className="col-12">
              <div className="text-muted mb-1">Comment *</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="rs-input"
                placeholder="Enter comments here..."
              />
            </div>

            {editingOther ? (
              <div className="col-12">
                <div className="text-muted mb-1">Reason for the change (optional)</div>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={2}
                  className="rs-input"
                  placeholder={`Included in the email to ${editOwnerName}`}
                />
              </div>
            ) : null}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button appearance="ghost" size="lg" onClick={() => setWizardStep(0)}>
              Back
            </Button>
            <Button appearance="primary" size="lg" onClick={submit} loading={submitLoading} disabled={!isReadyToSubmit}>
              {isAdminEdit ? 'Save Changes' : isEditMode ? 'Submit Modifications' : (needsApproval ? 'Submit Request' : 'Add Time Off')}
            </Button>
          </div>
        </Panel>
      ) : null}

      <Modal open={overlapModalOpen} onClose={() => setOverlapModalOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Potential Coverage Conflict</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hasTargetOverlap ? (
            <Message type="error" showIcon className="mb-3">
              {(actingOnBehalf || editingOther)
                ? `${(editingOther ? editOwnerName : selectedEmployeeLabel)} already has overlapping pending or approved time off. Please change the selected dates.`
                : 'You cannot create time off that overlaps your own existing pending or approved time off. Please change the selected dates.'}
            </Message>
          ) : (
            <Message type="warning" showIcon className="mb-3">
              Make sure all activities can be covered before continuing.
            </Message>
          )}

          <div className="mb-2">
            <div className="text-muted">{(actingOnBehalf || editingOther) ? `${(editingOther ? editOwnerName : selectedEmployeeLabel)}'s selected time-off` : 'Your selected time-off'}</div>
            <div>
              {rangeStart && rangeEnd ? `${formatLongDate(rangeStart)} to ${formatLongDate(rangeEnd)}` : '-'}
              {rangeStart && rangeEnd ? ` (${countDaysInclusive(rangeStart, rangeEnd)} days)` : ''}
            </div>
          </div>

          <div className="text-muted mb-1">Overlaps with</div>
          <ul className="mb-0 ps-3">
            {overlapSummary.map((item) => (
              <li key={item.userKey}>
                <strong>{item.isSelf ? `${item.userLabel} (${(actingOnBehalf || editingOther) ? 'Selected Employee' : 'You'})` : item.userLabel}</strong>
                {`: ${item.overlapRanges.map((range) => `${formatLongDate(range.start)} to ${formatLongDate(range.end)}`).join(', ')} (${item.overlapDays} day${item.overlapDays === 1 ? '' : 's'})`}
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {hasTargetOverlap ? (
            <NsiButton variant="primary" onClick={() => setOverlapModalOpen(false)}>
              Close
            </NsiButton>
          ) : (
            <>
              <NsiButton onClick={() => setOverlapModalOpen(false)}>
                Cancel
              </NsiButton>
              <NsiButton variant="primary" onClick={confirmOverlapAndContinue}>
                Confirm
              </NsiButton>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal open={closeDateCoverageModalOpen} onClose={() => setCloseDateCoverageModalOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Close Date Already Covers Selection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon className="mb-3">
            There is already a close date on this day, you do not need to schedule time off.
          </Message>
          <div>
            {rangeStart && rangeEnd ? `${formatLongDate(rangeStart)} to ${formatLongDate(rangeEnd)}` : '-'}
            {rangeStart && rangeEnd ? ` (${countDaysInclusive(rangeStart, rangeEnd)} days)` : ''}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setCloseDateCoverageModalOpen(false)}>
            Back
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={async () => {
              setCloseDateCoverageModalOpen(false);
              if (overlapSummary.length > 0) {
                setOverlapModalOpen(true);
                return;
              }
              setWizardStep(1);
            }}
          >
            Continue
          </NsiButton>
        </Modal.Footer>
      </Modal>

      {isEditMode && !editRequestNotAvailable ? (
        <div className="timeoff-edit-sticky-banner">
          <div className="timeoff-edit-sticky-banner__text">
            {isAdminEdit
              ? `You are editing ${editingOther ? `${editOwnerName}'s` : 'your'} ${editStatusLabel} request. It stays ${editStatusLabel}${editingOther ? `, and ${editOwnerName} will be emailed that it was edited` : ''}.`
              : 'You are editing an existing pending request. Submitting modifications cancels the current pending approval and creates a new request.'}
          </div>
          <Button appearance="subtle" color="red" size="sm" onClick={cancelModify}>
            Cancel Modify
          </Button>
        </div>
      ) : null}
    </div>
  );
}
